import { FC } from 'react';
import {
  Typography,
  Goal,
  Assist,
  Ticket,
  PenaltyMiss,
  PenaltySave,
  TransferInOut,
} from '@dreamteam-monorepo/ui';
import { EventCategory, EventEntry, EventsByCategory } from '../types';

const CategoryIcon: FC<{ category: EventCategory }> = ({ category }) => {
  switch (category) {
    case 'goal':
      return <Goal className="h-[16px] w-[16px] text-[--color-neurtal-1000] shrink-0" />;
    case 'ownGoal':
      return <Goal className="h-[16px] w-[16px] text-[--color-red-200] shrink-0" />;
    case 'assist':
      return <Assist className="h-[16px] w-[16px] text-[--color-neurtal-1000] shrink-0" />;
    case 'yellowCard':
      return <Ticket ticketType="yellow" className="h-[16px] w-[16px] shrink-0" />;
    case 'redCard':
      return <Ticket ticketType="red" className="h-[16px] w-[16px] shrink-0" />;
    case 'penaltyMiss':
      return <PenaltyMiss className="h-[16px] w-[16px] text-[--color-red-200] shrink-0" />;
    case 'penaltySave':
      return <PenaltySave className="h-[16px] w-[16px] text-[--color-blue-200] shrink-0" />;
    case 'subOn':
      return (
        <TransferInOut className="h-[16px] w-[16px] text-[--color-green-200] rotate-0 shrink-0" />
      );
    case 'subOff':
      return (
        <TransferInOut className="h-[16px] w-[16px] text-[--color-red-200] rotate-180 shrink-0" />
      );
    default:
      return <Goal className="h-[16px] w-[16px] text-[--color-neurtal-1000] shrink-0" />;
  }
};

const EventItem: FC<{ event: EventEntry; side: 'left' | 'right' }> = ({ event, side }) => (
  <div className="flex items-center gap-1 h-[24px]">
    {side === 'left' ? (
      <>
        <Typography
          variant="paragraph000"
          className="text-[--match-display-text] !font-bold text-[10px]"
        >
          {event.name}
        </Typography>
        {event.minutes.map((min, idx) => (
          <Typography
            key={idx}
            variant="paragraph000"
            className="text-[--match-display-text] text-[10px]"
          >
            {min}
          </Typography>
        ))}
        {event.isPenalty && (
          <Typography
            variant="paragraph000"
            className="text-[--match-display-text] !font-bold text-[10px]"
          >
            PEN
          </Typography>
        )}
      </>
    ) : (
      <>
        {event.isPenalty && (
          <Typography
            variant="paragraph000"
            className="text-[--match-display-text] !font-bold text-[10px]"
          >
            PEN
          </Typography>
        )}
        {event.minutes.map((min, idx) => (
          <Typography
            key={idx}
            variant="paragraph000"
            className="text-[--match-display-text] text-[10px]"
          >
            {min}
          </Typography>
        ))}
        <Typography
          variant="paragraph000"
          className="text-[--match-display-text] !font-bold text-[10px]"
        >
          {event.name}
        </Typography>
      </>
    )}
  </div>
);

const CenterEventSection: FC<{ section: EventsByCategory }> = ({ section }) => {
  const maxRows = Math.max(section.homeEvents.length, section.awayEvents.length, 1);

  return (
    <div className="flex items-start justify-center w-full">
      <div className="flex-1 flex flex-col justify-center pr-2 gap-0 min-h-[24px]">
        {Array.from({ length: maxRows }).map((_, i) =>
          section.homeEvents[i] ? (
            <div key={i} className="flex justify-end">
              <EventItem event={section.homeEvents[i]} side="left" />
            </div>
          ) : (
            <div key={i} className="h-[24px]" />
          )
        )}
      </div>

      <div className="w-6 flex justify-center pt-[2px]">
        <CategoryIcon category={section.category} />
      </div>

      <div className="flex-1 flex flex-col justify-center pl-2 gap-0 min-h-[24px]">
        {Array.from({ length: maxRows }).map((_, i) =>
          section.awayEvents[i] ? (
            <div key={i} className="flex justify-start">
              <EventItem event={section.awayEvents[i]} side="right" />
            </div>
          ) : (
            <div key={i} className="h-[24px]" />
          )
        )}
      </div>
    </div>
  );
};

const EdgeEventSection: FC<{ section: EventsByCategory }> = ({ section }) => {
  const maxRows = Math.max(section.homeEvents.length, section.awayEvents.length, 1);

  return (
    <div className="flex items-start w-full">
      <div className="flex-1 flex flex-col justify-center items-start gap-0 min-h-[24px]">
        {Array.from({ length: maxRows }).map((_, i) =>
          section.homeEvents[i] ? (
            <div key={i} className="flex items-center gap-1 h-[24px]">
              <Typography
                variant="paragraph000"
                className="text-[--match-display-text] !font-bold text-[10px]"
              >
                {section.homeEvents[i].name}
              </Typography>
              {section.homeEvents[i].minutes.map((min, idx) => (
                <Typography
                  key={idx}
                  variant="paragraph000"
                  className="text-[--match-display-text] text-[10px]"
                >
                  {min}
                </Typography>
              ))}
              <CategoryIcon category={section.category} />
            </div>
          ) : (
            <div key={i} className="h-[24px]" />
          )
        )}
      </div>

      <div className="w-6" />

      <div className="flex-1 flex flex-col justify-center items-end gap-0 min-h-[24px]">
        {Array.from({ length: maxRows }).map((_, i) =>
          section.awayEvents[i] ? (
            <div key={i} className="flex items-center gap-1 h-[24px]">
              <CategoryIcon category={section.category} />
              {section.awayEvents[i].minutes.map((min, idx) => (
                <Typography
                  key={idx}
                  variant="paragraph000"
                  className="text-[--match-display-text] text-[10px]"
                >
                  {min}
                </Typography>
              ))}
              <Typography
                variant="paragraph000"
                className="text-[--match-display-text] !font-bold text-[10px]"
              >
                {section.awayEvents[i].name}
              </Typography>
            </div>
          ) : (
            <div key={i} className="h-[24px]" />
          )
        )}
      </div>
    </div>
  );
};

export const MatchInfoWithIcons: FC<{
  eventSections: EventsByCategory[];
}> = ({ eventSections }) => {
  const centerCategories: EventCategory[] = ['goal', 'ownGoal', 'assist'];
  const edgeCategories: EventCategory[] = [
    'yellowCard',
    'redCard',
    'penaltyMiss',
    'penaltySave',
    'subOn',
    'subOff',
  ];

  const centerSections = eventSections.filter(s => centerCategories.includes(s.category));
  const edgeSections = eventSections.filter(s => edgeCategories.includes(s.category));

  return (
    <div className="flex flex-col gap-[--padding-xs] w-full mt-3 px-[--padding-xl]">
      {centerSections.map(section => (
        <CenterEventSection key={section.category} section={section} />
      ))}
      {/* {edgeSections.map(section => (
        <EdgeEventSection key={section.category} section={section} />
      ))} */}
    </div>
  );
};
